import { KA_MASKEN } from './ka/ka-maske';
import { GelesenesVerfahren } from './verfahren';

/**
 * Welche Reiter die Detailansicht bei einem Punkt zeigt, und welcher davon offen ist.
 *
 * **Eine KA-Aufnahme bekommt eigene Reiter, so wie die SEA ihre hat**
 * (Nutzer-Entscheidung 20.09.2026): je Block des amtlichen Bogens einen. Vorher stand
 * alles in einem Reiter untereinander, und weil ein Reiter in bpweb nicht von selbst
 * rollt, war die Hälfte unerreichbar.
 *
 * Die Beschriftung eines Reiters ist dieselbe wie die der Maske dahinter
 * (`KA_MASKEN`): Wer auf eine Zeile im Reiter „Horizonte" tippt, landet im Fenster
 * „Horizonte".
 */

/** Ein Reiter je Block des Bogens, in seiner Reihenfolge. */
export const KA_REITER_NAMEN: readonly string[] = KA_MASKEN.map(m => m.titel);

/**
 * Der einzige Reiter eines Punktes mit einem Verfahren, das diese Fassung der App
 * nicht kennt. Vier Reiter mit Feldern wären dort eine Behauptung: Wir wissen nicht,
 * wie dieser Punkt aufgebaut ist (siehe `verfahren.ts`, Regel 3).
 */
export const UNBEKANNT_REITER = 'Aufnahme';

/**
 * Reiter, die es bei jedem Punkt gibt, gleich nach welchem Verfahren.
 *
 * **Die Vegetation gehört dazu** (Nutzer-Entscheidung 20.09.2026): Eine Artenliste mit
 * Deckungsanteilen je Schicht ist verfahrensneutral, und der Bogen der KA fragt in
 * Feld 19 ohnehin nach der Vegetation. Was an diesem Reiter forstlich ist (Humusform,
 * Vegetationsformengruppe, Bestandesbeschreibung), bleibt bei einer KA-Aufnahme weg.
 */
const GEMEINSAME_REITER: readonly string[] = ['Vegetation', 'Karte', 'Hinweise'];

/**
 * Welcher Reiter der Detailansicht soll offen sein?
 *
 * Die Detailansicht merkt sich den zuletzt geöffneten Reiter über alle Punkte hinweg.
 * Wer eben an einem SEA-Punkt auf „Boden" stand und dann einen KA-Punkt öffnet, käme
 * sonst auf einen Reiter, den es dort nicht gibt.
 *
 * Für SEA-Punkte kommt `undefined` zurück: Dann gilt unverändert die bisherige Regel
 * der Detailansicht (Protokoll oder Kurzprotokoll je nach Punktart). Für den Rückweg
 * gibt es {@link reiterGehoertNichtHierher}.
 */
export function reiterFuerVerfahren(
	gemerkt: string | null | undefined,
	verfahren: GelesenesVerfahren,
): string | undefined {
	if (verfahren === 'sea95') return undefined;

	const name = gemerkt ?? '';
	if (GEMEINSAME_REITER.includes(name)) return name;

	if (verfahren === 'unbekannt') return UNBEKANNT_REITER;
	return KA_REITER_NAMEN.includes(name) ? name : KA_REITER_NAMEN[0];
}

/**
 * Gehört der gemerkte Reiter zu einem anderen Verfahren als dieser Punkt?
 *
 * **Der Fall, der lange offen war:** Der zuletzt geöffnete Reiter wird über ALLE Punkte
 * hinweg gemerkt. Für den Weg SEA → KA sorgt {@link reiterFuerVerfahren}; auf dem
 * Rückweg gab die Regel der Detailansicht einen unbekannten Namen unverändert zurück.
 * `activeId` zeigte dann auf einen Reiter, den es an diesem Punkt nicht gibt, und es war
 * GAR KEINER offen - eine leere Fläche, und niemand weiß, warum.
 *
 * Wer hier `true` bekommt, behandelt den gemerkten Reiter wie „nichts gemerkt" und
 * nimmt den Standard des Punktes.
 */
export function reiterGehoertNichtHierher(
	gemerkt: string | null | undefined,
	verfahren: GelesenesVerfahren,
): boolean {
	if (verfahren !== 'sea95') return false;
	const name = gemerkt ?? '';
	return KA_REITER_NAMEN.includes(name) || name === UNBEKANNT_REITER;
}
